import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Mail, Plus, Pencil, Trash2, Save, Eye, FileText, 
  Loader2, Star, Info, ChevronDown, ChevronRight, Copy
} from 'lucide-react';

const CATEGORIES = [
  { value: 'contact', label: 'General Contact', description: 'For general customer communications' },
  { value: 'job_update', label: 'Job Updates', description: 'Project status and progress updates' },
  { value: 'payment_reminder', label: 'Payment Reminders', description: 'Invoice and payment notifications' },
  { value: 'upcoming_service', label: 'Upcoming Services', description: 'Service reminder emails' },
  { value: 'thank_you', label: 'Thank You', description: 'Post-service thank you messages' },
  { value: 'request_confirmation', label: 'Request Confirmation', description: 'Request customer confirmation for scheduled work' },
  { value: 'custom', label: 'Custom', description: 'Custom templates for any purpose' }
];

const VARIABLES_BY_CATEGORY = {
  contact: ['customer_name', 'company_name', 'sender_name', 'message'],
  job_update: ['customer_name', 'job_title', 'job_status', 'job_address', 'scheduled_date', 'company_name', 'sender_name', 'message'],
  payment_reminder: ['customer_name', 'invoice_number', 'amount_due', 'due_date', 'company_name'],
  upcoming_service: ['customer_name', 'job_title', 'scheduled_date', 'job_address', 'confirmation_link', 'company_name'],
  thank_you: ['customer_name', 'job_title', 'company_name', 'sender_name'],
  request_confirmation: ['customer_name', 'job_title', 'job_address', 'scheduled_date', 'job_description', 'total_price', 'company_name', 'company_phone', 'company_email', 'sender_name', 'message'],
  custom: ['customer_name', 'company_name', 'sender_name', 'message']
};

function EmailTemplateManager({ apiClient }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('contact');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({
    name: '',
    category: 'contact',
    subject: '',
    body: '',
    is_default: false
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await apiClient.get('/email-templates');
      setTemplates(res.data);
    } catch (err) {
      toast.error('Failed to load templates');
    }
    setLoading(false);
  };

  const openCreateDialog = (category = 'contact') => {
    setEditingTemplate(null);
    setForm({
      name: '',
      category: category,
      subject: '',
      body: '',
      is_default: false
    });
    setEditDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      category: template.category,
      subject: template.subject,
      body: template.body,
      is_default: template.is_default
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.body) {
      toast.error('Name, subject, and body are required');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await apiClient.put(`/email-templates/${editingTemplate.id}`, form);
        toast.success('Template updated');
      } else {
        await apiClient.post('/email-templates', form);
        toast.success('Template created');
      }
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      await apiClient.delete(`/email-templates/${templateToDelete.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const setAsDefault = async (template) => {
    try {
      await apiClient.put(`/email-templates/${template.id}`, { is_default: true });
      toast.success(`"${template.name}" is now the default for ${CATEGORIES.find(c => c.value === template.category)?.label}`);
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to set default');
    }
  };

  const getTemplatesByCategory = (category) => {
    return templates.filter(t => t.category === category);
  };

  const previewText = (text) => {
    return text
      .replace(/\{\{customer_name\}\}/g, 'John Smith')
      .replace(/\{\{company_name\}\}/g, 'Demo Landscaping')
      .replace(/\{\{sender_name\}\}/g, 'Mike Johnson')
      .replace(/\{\{job_title\}\}/g, 'Weekly Lawn Maintenance')
      .replace(/\{\{job_status\}\}/g, 'In Progress')
      .replace(/\{\{job_address\}\}/g, '123 Main St, Springfield')
      .replace(/\{\{scheduled_date\}\}/g, 'March 15, 2026')
      .replace(/\{\{invoice_number\}\}/g, 'INV-1234')
      .replace(/\{\{amount_due\}\}/g, '450.00')
      .replace(/\{\{due_date\}\}/g, 'March 20, 2026')
      .replace(/\{\{confirmation_link\}\}/g, '[Confirmation Link]')
      .replace(/\{\{message\}\}/g, '[Your custom message will appear here]');
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            Email Templates
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Manage email templates used throughout the app
          </p>
        </div>
        <Button onClick={() => openCreateDialog()} className="bg-primary text-black">
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {CATEGORIES.map(category => {
          const categoryTemplates = getTemplatesByCategory(category.value);
          const isExpanded = expandedCategory === category.value;
          
          return (
            <Card key={category.value} className="bg-zinc-900 border-zinc-800">
              <CardHeader 
                className="cursor-pointer hover:bg-zinc-800/50 transition-colors rounded-t-lg"
                onClick={() => setExpandedCategory(isExpanded ? '' : category.value)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-zinc-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-zinc-400" />
                    )}
                    <div>
                      <CardTitle className="text-white text-lg">{category.label}</CardTitle>
                      <CardDescription className="text-zinc-500">{category.description}</CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline" className="border-zinc-600 text-zinc-400">
                    {categoryTemplates.length} template{categoryTemplates.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-0">
                  {categoryTemplates.length === 0 ? (
                    <div className="text-center py-6 text-zinc-500">
                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No templates in this category</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 border-zinc-600"
                        onClick={() => openCreateDialog(category.value)}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Create Template
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {categoryTemplates.map(template => (
                        <div 
                          key={template.id}
                          className={`bg-zinc-800/50 rounded-lg p-4 border-l-4 ${
                            template.is_default ? 'border-l-primary' : 'border-l-zinc-600'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h4 className="text-white font-medium">{template.name}</h4>
                                {template.is_default && (
                                  <Badge className="bg-primary/20 text-primary border-0">
                                    <Star className="w-3 h-3 mr-1 fill-current" /> Default
                                  </Badge>
                                )}
                                {template.is_system && (
                                  <Badge variant="outline" className="border-zinc-500 text-zinc-400 text-xs">
                                    System
                                  </Badge>
                                )}
                              </div>
                              <p className="text-zinc-400 text-sm truncate">
                                Subject: {template.subject}
                              </p>
                              <p className="text-zinc-500 text-xs mt-1 line-clamp-2">
                                {template.body.substring(0, 150)}...
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {!template.is_default && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setAsDefault(template)}
                                  className="text-zinc-400 hover:text-primary"
                                  title="Set as Default"
                                >
                                  <Star className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(template)}
                                className="text-zinc-400 hover:text-white"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {!template.is_system && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setTemplateToDelete(template);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-zinc-400 hover:text-red-400"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full border-zinc-700 text-zinc-400 hover:text-white"
                        onClick={() => openCreateDialog(category.value)}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Template to {category.label}
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Template Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Project Update"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Category *</Label>
                <Select 
                  value={form.category} 
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value} className="text-white">
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">Subject *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Email subject line..."
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Preview: {previewText(form.subject)}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-zinc-300">Email Body *</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-zinc-400 hover:text-white"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {showPreview ? 'Edit' : 'Preview'}
                </Button>
              </div>
              
              {showPreview ? (
                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 min-h-[200px] text-white whitespace-pre-wrap">
                  {previewText(form.body)}
                </div>
              ) : (
                <Textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Email body content..."
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  rows={10}
                />
              )}
            </div>

            {/* Variables Help */}
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
                <Info className="w-4 h-4" />
                Available Variables for {CATEGORIES.find(c => c.value === form.category)?.label}
              </div>
              <div className="flex flex-wrap gap-2">
                {(VARIABLES_BY_CATEGORY[form.category] || []).map(v => (
                  <Badge
                    key={v}
                    variant="outline"
                    className="border-zinc-600 text-zinc-300 cursor-pointer hover:bg-zinc-700"
                    onClick={() => {
                      const variable = `{{${v}}}`;
                      setForm({ ...form, body: form.body + variable });
                      toast.success(`Added ${variable}`);
                    }}
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    {`{{${v}}}`}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Switch
                id="is_default"
                checked={form.is_default}
                onCheckedChange={(checked) => setForm({ ...form, is_default: checked })}
              />
              <Label htmlFor="is_default" className="text-zinc-300 cursor-pointer">
                Set as default template for this category
              </Label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-zinc-600">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary text-black">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingTemplate ? 'Update' : 'Create'} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Template</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default EmailTemplateManager;
